import { format, formatDistanceToNow } from "date-fns"
import type { ComponentProps, KeyboardEvent, ReactNode } from "react"
import { createContext, useContext } from "react"

import { StatusBadge, type StatusTier } from "@/components/ui/badge"
import { Link } from "@/components/ui/link"
import {
  Table as KitTable,
  TableCard as KitTableCard,
  TableRowActionsDropdown,
} from "@/components/ui-kit/untitled/application/table/table"

import { cx } from "@uui/utils/cx"

/*
 * The ledger — the console table (Master Design System §12).
 *
 * caption  the print band is the sheet's caption: condensed title, mono
 *          counts on-print, applied filters as on-print tokens, the filter
 *          field ("/" focuses it). The band always states what is shown.
 * header   labels in the stone.100 well between stone.300 rules (ledger.header,
 *          ledger.header-rule): mono 500 10px caps in stone.800; the sorted
 *          column and its arrow in ink.
 * rows     44px compact / 48px two-line, separators ink @7%. Rest → hover
 *          (page wash, entity underline, actions reveal) → focus (2px print.700
 *          inset ring) → selected (the half-step, stone.100 at 50% over paper,
 *          the lead at 600, the tick in ink; hover adds its reveals, never a
 *          second step of stone). The ROW is
 *          the link: the entity
 *          in column one is ink 500; print.700 is reserved for true inline
 *          links (`Table.Link`).
 * columns  words Sans 13 @72–78% · notation mono 11 @72% · comparable
 *          numerals right-aligned, tabular, 500 @78%.
 * status   grouped by state before coloured by state (`Table.Group`); an
 *          overdue row is a caution wash with an inset 3px solid rule and a
 *          mono chip beside the date (`Table.Chip`).
 * footer   says where the list ends and carries the keyboard legend
 *          (j/k move · enter opens · / filters).
 *
 * Held back on purpose: zebra striping, drag-to-reorder, accordion rows,
 * per-cell menus, a sticky first column, row-level primary buttons. Not yet
 * built: selection and bulk mode, sortable label buttons, the column menu,
 * the entity peek card, copy-on-hover cells, inline numeral editing.
 *
 * The vendored kit is not patched: every house class lands through twMerge,
 * where the later class wins, so the kit's own defaults are simply replaced.
 */

type Align = "left" | "right"
type Rows = "compact" | "two-line"
type Tone = "caution" | "remote"
type ChipTier = "caution" | "remote" | "confirmed"

const NOTATION = "font-mono text-xs leading-4 tabular-nums text-tertiary"

/** A date-only ISO string is a calendar day, not a UTC instant. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const parseAt = (at: string) =>
  new Date(DATE_ONLY.test(at) ? `${at}T00:00:00` : at)

const stringClass = (className: unknown) =>
  typeof className === "string" ? className : undefined

const isEmpty = (children: ReactNode) =>
  children === null || children === undefined || children === ""

const LedgerContext = createContext<{ rows: Rows }>({ rows: "compact" })

/* --- The table ------------------------------------------------------------
 * Cells do not wrap: a ledger reads across, and the card scrolls sideways on a
 * phone. A prose column opts back in with `whitespace-normal`, or truncates.
 */

type RootProps = ComponentProps<typeof KitTable> & {
  /** `two-line` houses a lead cell with a sub line (48px rows). */
  rows?: Rows
}

function Root({
  className,
  size = "sm",
  rows = "compact",
  ...props
}: RootProps) {
  return (
    <LedgerContext.Provider value={{ rows }}>
      <KitTable
        size={size}
        className={cx("bg-primary", stringClass(className))}
        {...props}
      />
    </LedgerContext.Provider>
  )
}

type HeaderProps = Parameters<typeof KitTable.Header>[0]

function Header({ className, ...props }: HeaderProps) {
  return (
    <KitTable.Header
      className={cx(
        "bg-ledger-header h-ledger-header shadow-[inset_0_1px_0_var(--color-ledger-header-rule),inset_0_-1px_0_var(--color-ledger-header-rule)]",
        stringClass(className),
      )}
      {...props}
    />
  )
}

interface HeadProps extends Omit<
  ComponentProps<typeof KitTable.Head>,
  "label" | "children"
> {
  label?: string
  align?: Align
  /** The column the list is ordered by: the label and its arrow go ink. */
  sorted?: "asc" | "desc"
  children?: ReactNode
}

function Head({
  label,
  align = "left",
  sorted,
  className,
  children,
  ...props
}: HeadProps) {
  const text = label ?? (typeof children === "string" ? children : undefined)
  return (
    <KitTable.Head
      {...props}
      className={cx(
        "px-4 py-0",
        align === "right" && "[&>div]:justify-end",
        stringClass(className),
      )}
    >
      {text !== undefined ? (
        <span
          className={cx(
            "text-2xs tracking-label inline-flex items-center gap-1.5 font-mono leading-none font-medium whitespace-nowrap uppercase",
            sorted ? "text-primary" : "text-ledger-label",
          )}
        >
          {text}
          {sorted && (
            <span aria-hidden="true" className="tracking-normal">
              {sorted === "asc" ? "↑" : "↓"}
            </span>
          )}
        </span>
      ) : (
        children
      )}
    </KitTable.Head>
  )
}

type RowProps = Parameters<typeof KitTable.Row>[0] & {
  /** `caution` = overdue / needs a person; `remote` = scheduled / fetched on view. */
  tone?: Tone
}

const TONE: Record<Tone, string> = {
  caution:
    "bg-caution-50 hover:bg-caution-50 shadow-[inset_3px_0_0_var(--color-caution-500)]",
  // status.remote: a dashed print.700 edge and no wash (MDS D2) — fetched on
  // view or scheduled, not stored. Focus keeps its solid ring.
  remote:
    "outline-1 outline-dashed -outline-offset-1 outline-print-700 focus-visible:outline-solid",
}

function Row({ className, tone, href, ...props }: RowProps) {
  const { rows } = useContext(LedgerContext)
  return (
    <KitTable.Row
      href={href}
      highlightSelectedRow={false}
      className={cx(
        "group outline-focus-ring hover:bg-ledger-hover data-[selected]:bg-ledger-selected data-[selected]:hover:bg-ledger-selected-hover [&>td]:after:bg-ledger-rule",
        rows === "two-line" ? "h-12" : "h-11",
        href && "cursor-pointer",
        tone && TONE[tone],
        stringClass(className),
      )}
      {...props}
    />
  )
}

interface CellProps extends Omit<
  ComponentProps<typeof KitTable.Cell>,
  "className" | "children"
> {
  align?: Align
  className?: string
  children?: ReactNode
}

function Cell({ className, align = "left", ...props }: CellProps) {
  return (
    <KitTable.Cell
      {...props}
      className={cx(
        "text-tertiary px-4 py-0 text-sm whitespace-nowrap",
        align === "right" && "text-right",
        className,
      )}
    />
  )
}

/* --- Cell helpers --------------------------------------------------------- */

/** The entity in column one: ink 500, underlined when the row is hovered or focused. The row is the link. */
function Lead({
  sub,
  className,
  children,
  ...props
}: CellProps & { sub?: ReactNode }) {
  return (
    <Cell
      {...props}
      className={cx(
        "text-primary font-medium group-data-[selected]:font-semibold",
        className,
      )}
    >
      <span className="decoration-1 underline-offset-[3px] group-hover:underline group-data-[focus-visible]:underline">
        {children}
      </span>
      {!isEmpty(sub) && (
        <span
          className={cx(
            "mt-1 block font-normal",
            typeof sub === "string" && "text-tertiary text-xs",
          )}
        >
          {sub}
        </span>
      )}
    </Cell>
  )
}

/** A true inline link inside a cell — print, and it does not trigger the row. */
function InlineLink({ className, ...props }: ComponentProps<typeof Link>) {
  return (
    <Link
      {...props}
      className={cx(
        "decoration-1 underline-offset-[3px] hover:underline",
        typeof className === "string" ? className : undefined,
      )}
    />
  )
}

/** A timestamp: mono, tabular, right-aligned; optionally how long ago, and a state chip beside it. */
function When({
  at,
  pattern = "d MMM yyyy · HH:mm",
  relative = false,
  chip,
  className,
  ...props
}: CellProps & {
  at: string | null | undefined
  pattern?: string
  relative?: boolean
  chip?: ReactNode
}) {
  return (
    <Cell
      {...props}
      align={props.align ?? "right"}
      className={cx(NOTATION, "whitespace-nowrap", className)}
    >
      {at ? (
        <>
          <span className="inline-flex items-center gap-2">
            <time dateTime={at}>{format(parseAt(at), pattern)}</time>
            {chip}
          </span>
          {relative && (
            <span className="text-quaternary block">
              {formatDistanceToNow(parseAt(at), { addSuffix: true })}
            </span>
          )}
        </>
      ) : (
        "—"
      )}
    </Cell>
  )
}

/** An id, token, phone number or code: mono, never wrapped. */
function Id({ className, children, ...props }: CellProps) {
  return (
    <Cell {...props} className={cx(NOTATION, "whitespace-nowrap", className)}>
      {isEmpty(children) ? "—" : children}
    </Cell>
  )
}

/** A comparable numeral: mono 500 at 78%, tabular, right-aligned, with an optional unit. */
function Figure({
  unit,
  className,
  children,
  ...props
}: CellProps & { unit?: string }) {
  return (
    <Cell
      {...props}
      align={props.align ?? "right"}
      className={cx(
        NOTATION,
        "text-secondary font-medium whitespace-nowrap",
        className,
      )}
    >
      {isEmpty(children) ? (
        "—"
      ) : (
        <>
          {children}
          {unit && <span className="text-tertiary font-normal"> {unit}</span>}
        </>
      )}
    </Cell>
  )
}

/** Status as one of the four tiers; nothing to say renders as a quiet dash. */
function Status({
  tier,
  className,
  children,
  ...props
}: CellProps & { tier: StatusTier }) {
  return (
    <Cell {...props} className={cx("whitespace-nowrap", className)}>
      {isEmpty(children) ? (
        <span className="text-quaternary">—</span>
      ) : (
        <StatusBadge tier={tier}>{children}</StatusBadge>
      )}
    </Cell>
  )
}

/**
 * State as a chip beside the datum — the datum itself stays ink, prose is
 * never tinted. caution.50 ground, .500 dot, .700 mono; remote and confirmed
 * follow the same shape in their own grammar.
 */
const CHIP: Record<ChipTier, { box: string; dot: string }> = {
  caution: {
    box: "bg-caution-50 border-caution-500/40 text-caution-700",
    dot: "bg-caution-500",
  },
  remote: {
    box: "border-dashed border-print-700 text-print-700",
    dot: "bg-print-600",
  },
  confirmed: {
    box: "bg-do-50 border-do-500/40 text-do-800",
    dot: "bg-do-500",
  },
}

function Chip({
  tier,
  className,
  children,
}: {
  tier: ChipTier
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-[3px] font-mono text-[9.5px] leading-none font-semibold",
        CHIP[tier].box,
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cx("size-[5px] rounded-full", CHIP[tier].dot)}
      />
      {children}
    </span>
  )
}

/**
 * The trailing actions column: 46px, revealed on row hover and focus, always
 * present where there is no hover to reveal it.
 */
function Actions({ className, ...props }: CellProps) {
  return (
    <Cell
      {...props}
      align={props.align ?? "right"}
      className={cx(
        "w-ledger-actions px-2.5 py-0 whitespace-nowrap [&>*]:opacity-0 [&>*]:transition-opacity [&>*]:duration-(--duration-snap) group-focus-within:[&>*]:opacity-100 group-hover:[&>*]:opacity-100 group-data-[focus-visible]:[&>*]:opacity-100 [@media(hover:none)]:[&>*]:opacity-100",
        className,
      )}
    />
  )
}

/**
 * A group head between rows: the queue is grouped by state before it is
 * coloured by state. Renders as a full row, so pass the column count.
 */
const GROUP: Record<
  "caution" | "remote" | "quiet",
  { text: string; dot: string | null }
> = {
  caution: { text: "text-caution-700 font-semibold", dot: "bg-caution-500" },
  remote: { text: "text-print-700 font-semibold", dot: "bg-print-600" },
  quiet: { text: "text-quaternary font-medium", dot: null },
}

function Group({
  id,
  label,
  count,
  tier = "quiet",
  span,
}: {
  id: string
  label: string
  count?: number
  tier?: "caution" | "remote" | "quiet"
  span: number
}) {
  const style = GROUP[tier]
  return (
    <KitTable.Row
      id={id}
      isDisabled
      highlightSelectedRow={false}
      className="[&>td]:after:bg-border-tertiary h-[30px] hover:bg-transparent"
    >
      <KitTable.Cell className="px-4 py-0 whitespace-nowrap">
        <span
          className={cx(
            "text-2xs tracking-label inline-flex items-center gap-2 font-mono leading-none uppercase",
            style.text,
          )}
        >
          {style.dot && (
            <span
              aria-hidden="true"
              className={cx("size-[7px] rounded-full", style.dot)}
            />
          )}
          {label}
          {count !== undefined && ` · ${count}`}
        </span>
      </KitTable.Cell>
      {Array.from({ length: Math.max(span - 1, 0) }, (_, i) => (
        <KitTable.Cell key={i} className="p-0" />
      ))}
    </KitTable.Row>
  )
}

/* --- The card: caption, filter, footer ----------------------------------- */

const isEditable = (el: EventTarget | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName))

/** j/k move between rows, enter opens (the row is a link), "/" focuses the filter. */
function ledgerKeys(e: KeyboardEvent<HTMLDivElement>) {
  if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return
  if (isEditable(e.target)) return
  const card = e.currentTarget

  if (e.key === "/") {
    const filter = card.querySelector<HTMLElement>("[data-ledger-filter]")
    if (filter) {
      e.preventDefault()
      filter.focus()
    }
    return
  }

  if (e.key === "j" || e.key === "k") {
    const rows = Array.from(
      card.querySelectorAll<HTMLElement>(
        "tbody [role=row]:not([data-disabled])",
      ),
    )
    if (rows.length === 0) return
    const current = (e.target as HTMLElement).closest<HTMLElement>("[role=row]")
    const index = current ? rows.indexOf(current) : -1
    const next =
      e.key === "j"
        ? rows[Math.min(index + 1, rows.length - 1)]
        : rows[Math.max(index - 1, 0)]
    if (next) {
      e.preventDefault()
      next.focus()
    }
  }
}

type CardRootProps = ComponentProps<typeof KitTableCard.Root> & {
  /** Run edge to edge on a phone, and become a card again from `lg`. */
  bleed?: boolean
}

function CardRoot({
  className,
  size = "sm",
  bleed = false,
  onKeyDown,
  ...props
}: CardRootProps) {
  return (
    <KitTableCard.Root
      size={size}
      onKeyDown={(e) => {
        onKeyDown?.(e)
        ledgerKeys(e)
      }}
      className={cx(
        bleed && "-mx-4 rounded-none lg:mx-0 lg:rounded-xl",
        className,
      )}
      {...props}
    />
  )
}

export interface CaptionToken {
  label: string
  onClear?: () => void
}

/**
 * The print band. `count` is what is shown ("14 check-ins"), `alert` what
 * needs a person ("4 overdue"), `tokens` the applied filters, `note` the
 * ordering ("grouped · overdue first · date ↑"), `trailing` the filter field.
 */
function Caption({
  title,
  count,
  alert,
  tokens,
  note,
  trailing,
  className,
}: {
  title: ReactNode
  count?: ReactNode
  alert?: ReactNode
  tokens?: CaptionToken[]
  note?: ReactNode
  trailing?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cx(
        "bg-ledger-caption flex flex-wrap items-center justify-between gap-x-3.5 gap-y-2 py-2.5 pr-3.5 pl-5 text-stone-50",
        className,
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2.5">
        <h2 className="text-[15px] leading-none text-stone-50">{title}</h2>
        {!isEmpty(count) && (
          <span className="text-onprint-emphasis text-2xs font-mono leading-none">
            {count}
          </span>
        )}
        {!isEmpty(alert) && (
          <span className="text-caution-500 text-2xs font-mono leading-none font-medium">
            · {alert}
          </span>
        )}
        {tokens?.map((token) => (
          <span
            key={token.label}
            className="border-onprint-edge text-2xs inline-flex items-center gap-1.5 rounded border bg-stone-50/12 px-2 py-1 font-mono leading-none font-medium text-stone-50/85"
          >
            {token.label}
            {token.onClear && (
              <button
                type="button"
                onClick={token.onClear}
                aria-label={`Clear ${token.label}`}
                className="text-onprint-quiet -m-0.5 rounded-xs p-0.5 outline-none hover:text-stone-50 focus-visible:outline-2 focus-visible:outline-stone-50"
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {(trailing || !isEmpty(note)) && (
        <div className="flex flex-wrap items-center gap-3.5">
          {!isEmpty(note) && (
            <span className="text-onprint-emphasis text-2xs font-mono leading-none">
              {note}
            </span>
          )}
          {trailing}
        </div>
      )}
    </div>
  )
}

/** The filter, living in the caption. "/" focuses it; enter commits. */
function Filter({
  value,
  onChange,
  onCommit,
  placeholder = "filter",
  label = "Filter",
  className,
}: {
  value: string
  onChange: (value: string) => void
  onCommit?: (value: string) => void
  placeholder?: string
  label?: string
  className?: string
}) {
  return (
    <label
      className={cx(
        "text-onprint-quiet border-onprint-edge text-2xs flex cursor-text items-center gap-2 rounded border px-2.5 py-[5px] font-mono leading-none transition-colors duration-(--duration-snap) focus-within:border-stone-50/55 focus-within:text-stone-50 hover:border-stone-50/50",
        className,
      )}
    >
      <input
        data-ledger-filter
        aria-label={label}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit?.(value)
          if (e.key === "Escape") (e.target as HTMLInputElement).blur()
        }}
        onBlur={() => onCommit?.(value)}
        className="placeholder:text-onprint-quiet text-2xs w-40 bg-transparent font-mono leading-none text-stone-50 outline-none"
      />
      <kbd
        aria-hidden="true"
        className="border-onprint-edge text-3xs rounded-sm border px-1.5 py-0.5 leading-none"
      >
        /
      </kbd>
    </label>
  )
}

/** Where the list ends, and the keyboard legend at rest. */
function Footer({
  shown,
  total,
  filters = false,
  trailing,
  children,
  className,
}: {
  shown?: number
  total?: number
  /** Whether the caption carries a filter field, so the legend can say so. */
  filters?: boolean
  trailing?: ReactNode
  children?: ReactNode
  className?: string
}) {
  const count =
    total === undefined
      ? null
      : shown !== undefined && shown < total
        ? `showing ${shown} of ${total}`
        : `that's all ${total}`
  const legend = ["j/k move", "enter opens", filters && "/ filters"]
    .filter(Boolean)
    .join(" · ")
  return (
    <div
      className={cx(
        "border-ledger-rule text-quaternary text-2xs flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t px-5 py-2.5 font-mono leading-none",
        className,
      )}
    >
      <span>{children ?? [count, legend].filter(Boolean).join(" · ")}</span>
      {trailing}
    </div>
  )
}

/**
 * Empty, in the certainty grammar: one provisional slot where a row would be,
 * and one forward-looking sentence at body 72%. No illustration.
 */
function Empty({
  children,
  action,
  className,
}: {
  children: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cx(
        "flex flex-col items-center gap-4 px-6 py-10 text-center",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="provisional-slot block h-[11px] w-52 max-w-full"
      />
      <p className="text-tertiary max-w-[44ch] text-sm text-pretty">
        {children}
      </p>
      {action}
    </div>
  )
}

export const Table = Object.assign(Root, {
  Body: KitTable.Body,
  Header,
  Head,
  Row,
  Group,
  Cell,
  Lead,
  Link: InlineLink,
  When,
  Id,
  Figure,
  Status,
  Chip,
  Actions,
})

export const TableCard = {
  Root: CardRoot,
  Caption,
  Filter,
  Empty,
  Footer,
}

export { TableRowActionsDropdown }
export type {
  Align as TableAlign,
  CellProps as TableCellProps,
  Rows as TableRows,
  Tone as TableTone,
}
